/**
 * @file 쿠키 세션 로그인 상태
 * @description 토큰은 HttpOnly 쿠키라 JS 로 못 읽는다. 로그인 정보는 서버 me 로 받아서 여기(메모리)에 둔다
 *              - 응답 도메인(respond, i)에서는 아무것도 하지 않는다 (설문 응답은 로그인과 무관)
 *              - 같은 브라우저의 탭, iframe 끼리는 BroadcastChannel 로 로그인 상태를 맞춘다
 *              - 재로그인 모달, 미사용 경고는 최상위 창에서만 띄운다 (iframe 은 최상위에 요청)
 */
import axios, { AxiosRequestConfig } from 'axios'
import { cookies } from 'utils'
import decodeJwt from 'ts-utils/decodeJwt'

type Me = { [key: string]: any } | null

export type ReloginReason = 'NONE' | 'EXPIRED' | 'REVOKED' | 'IDLE' | 'LEGACY' | 'INVALID' | 'CSRF' | 'LOGOUT'

type ReloginState = {
    open: boolean;
    reason: ReloginReason;
    expectUserNo: number | null;
}

const LOGINED_COOKIE = 'Logined'
const LOGINED_USER_COOKIE = 'LoginedUser'
const ACTIVITY_KEY = 'lsv_last_activity'

// 미사용 자동 로그아웃: 전 환경 3시간. 경고는 로그아웃 5분 전
// 서버(survey-graphql utils/auth.js IDLE_MINUTES)는 여기에 5분 여유를 더해 끊는다
export const IDLE_LOGOUT_MINUTES = 180
export const IDLE_LOGOUT_MS = IDLE_LOGOUT_MINUTES * 60 * 1000
export const IDLE_WARN_MS = IDLE_LOGOUT_MS - 5 * 60 * 1000

// 재로그인 모달, 미사용 경고는 화면의 모든 것(전체 로딩 화면 drawer+1000000, 알림 10000000 등) 위에 뜬다
export const RELOGIN_Z_INDEX = 30000000
export const IDLE_WARN_Z_INDEX = RELOGIN_Z_INDEX - 1

export const isRespondHost = () => {
    const { hostname } = window.location
    return hostname === process.env.REACT_APP_RESPOND_DOMAIN || hostname === process.env.REACT_APP_NEW_RESPOND_DOMAIN
}

export const isTopWindow = () => {
    try {
        return window.top === window.self
    } catch (e) {
        return false
    }
}

/*
** 로그인 표시 쿠키 (화면 표시용 표시값, 비밀값 아님)
** 세션 쿠키(서버, 15일)와 같이 브라우저를 껐다 켜도 남는다 → 다시 열면 그대로 로그인, 미사용 시간 지났으면 재로그인 모달
** 없으면(이 브라우저에서 로그인한 적 없음, 로그아웃함) 모달이 아니라 로그인 페이지로
*/
const LOGINED_DAYS = 15

const cookieDomain = () => (window.location.hostname === 'localhost' ? 'localhost' : `.${process.env.REACT_APP_DOMAIN}`)

export const setLoginedCookie = (user_no: number) => {
    cookies.set({ value: `${LOGINED_COOKIE}=true`, expires: LOGINED_DAYS, domain: cookieDomain() })
    cookies.set({ value: `${LOGINED_USER_COOKIE}=${user_no}`, expires: LOGINED_DAYS, domain: cookieDomain() })
}

export const removeLoginedCookie = () => {
    cookies.remove({ name: LOGINED_COOKIE, domain: cookieDomain() })
    cookies.remove({ name: LOGINED_USER_COOKIE, domain: cookieDomain() })
}

// 로그인이 풀렸을때 (재로그인 모달). www 는 Logined=true 면 로그인창 없이 바로 작업함으로 보내므로 꺼둔다
// LoginedUser 는 남긴다 → 이 브라우저에서 쓰던 계정이므로 새로고침해도 로그인 페이지가 아니라 재로그인 모달
const markLoginedOut = () => {
    if (cookies.get(LOGINED_COOKIE) === 'false') return
    cookies.set({ value: `${LOGINED_COOKIE}=false`, expires: LOGINED_DAYS, domain: cookieDomain() })
}

const getLoginedUserNo = () => {
    const user_no = Number(cookies.get(LOGINED_USER_COOKIE))
    return user_no > 0 ? user_no : null
}

/*
** 탭, iframe 간 동기화
*/
const channel: BroadcastChannel | null = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('lsv-auth') : null

const broadcast = (message: any) => {
    if (channel) channel.postMessage(message)
}

/*
** 로그인 정보 (me)
*/
let me: Me = null
let meLoadedAt = 0
const meListeners = new Set<() => void>()

export const getMe = () => me
export const getMeLoadedAt = () => meLoadedAt

// 로그인 여부만 (App, AuthGate 용). me 내용이 바뀔때마다 다시 그리면 라우트 화면이 통째로 다시 마운트된다
export const getHasMe = () => Boolean(me)

export const subscribeMe = (listener: () => void) => {
    meListeners.add(listener)
    return () => { meListeners.delete(listener) }
}

export const setMe = (value: Me, options: { broadcast?: boolean } = { broadcast: true }) => {
    meLoadedAt = Date.now()

    // 내용이 같으면 알리지 않는다 (updateToken 이 자주 불려도 화면이 다시 그려지지 않게)
    if (JSON.stringify(me) === JSON.stringify(value)) return

    me = value
    meListeners.forEach(c => c())

    if (options.broadcast) broadcast({ type: 'me', me: value })
}

/*
** 재로그인 모달 상태
*/
let relogin: ReloginState = { open: false, reason: 'NONE', expectUserNo: null }
const reloginListeners = new Set<() => void>()

// iframe 은 모달을 최상위 창에 요청만 한다. 로그인될때까지 여기서도 모달이 떠 있는 것으로 본다
let reloginRequested = false

export const getRelogin = () => relogin

// 재로그인 모달이 떠 있는 동안 (이 창이든 최상위 창이든). 이때 난 요청 실패는 에러 화면을 띄우지 않는다
export const isReloginOpen = () => relogin.open || reloginRequested

export const subscribeRelogin = (listener: () => void) => {
    reloginListeners.add(listener)
    return () => { reloginListeners.delete(listener) }
}

const setRelogin = (value: ReloginState) => {
    relogin = value
    reloginListeners.forEach(c => c())
}

// 로그인 문제 발생 → 최상위 창에 모달 (이미 떠 있으면 하나로 합친다)
export const openRelogin = (reason: ReloginReason = 'NONE', expectUserNo: number | null = null) => {
    if (isRespondHost()) return

    markLoginedOut()

    if (!isTopWindow()) {
        reloginRequested = true
        broadcast({ type: 'need-login', reason, expectUserNo: expectUserNo || (me && me.user_no) || null })
        return
    }

    if (relogin.open) return

    setRelogin({
        open: true,
        reason,
        expectUserNo: expectUserNo || (me && me.user_no) || relogin.expectUserNo || getLoginedUserNo()
    })
}

// 로그인이 풀려 실패한 읽기 요청(query)은 로그인 될때까지 기다렸다가 다시 보낸다 (index.js errorLink)
// 쓰기 요청(저장 등)은 기다리지 않는다 → 로그인 후 사용자 모르게 저장되지 않게
let loginWaiters: Array<() => void> = []

export const waitForLogin = () => new Promise<void>(resolve => { loginWaiters.push(resolve) })

const resolveLoginWaiters = () => {
    const waiters = loginWaiters
    loginWaiters = []
    waiters.forEach(c => c())
}

// 로그인 성공 (모달, Ing, 다른 탭에서)
// 재로그인(모달)은 모달을 닫고 실패했던 읽기 요청만 다시 보낸다. 쓰던 로그인 정보는 그대로 둬서 화면이 통째로 다시 그려지지 않게 한다
// (앱 시작때 로그인이 풀려 로그인 정보가 아예 없던 경우만 채운다. 없으면 화면을 그릴수 없다)
export const loginSucceeded = (value: Me, options: { broadcast?: boolean } = { broadcast: true }) => {
    if (value && value.user_no) setLoginedCookie(value.user_no)

    const isRelogin = relogin.open || reloginRequested
    const keepMe = isRelogin && me && value && me.user_no === value.user_no

    if (!keepMe) setMe(value, { broadcast: false })

    reloginRequested = false
    setRelogin({ open: false, reason: 'NONE', expectUserNo: null })
    markActivity()
    resolveLoginWaiters()

    if (options.broadcast) broadcast({ type: 'login', me: value })
}

if (channel) {
    channel.onmessage = (e: MessageEvent) => {
        const { data } = e
        if (!data || typeof data !== 'object') return

        if (data.type === 'me') setMe(data.me, { broadcast: false })
        else if (data.type === 'login') loginSucceeded(data.me, { broadcast: false })
        else if (data.type === 'need-login' && isTopWindow()) openRelogin(data.reason, data.expectUserNo)
        else if (data.type === 'idle-logout' && isTopWindow()) openRelogin('IDLE')
    }
}

/*
** 사용자 활동 시각 (탭, iframe 공유). 비밀값 아님
*/
let lastActivityMemory = Date.now()

export const markActivity = () => {
    lastActivityMemory = Date.now()
    try {
        localStorage.setItem(ACTIVITY_KEY, String(lastActivityMemory))
    } catch (e) {}
}

export const getLastActivity = () => {
    try {
        const value = Number(localStorage.getItem(ACTIVITY_KEY))
        return Math.max(value || 0, lastActivityMemory)
    } catch (e) {
        return lastActivityMemory
    }
}

// 저장된 마지막 활동 시각만 (이 창이 막 열린 시각은 빼고). 없으면 0
const getStoredActivity = () => {
    try {
        return Number(localStorage.getItem(ACTIVITY_KEY)) || 0
    } catch (e) {
        return 0
    }
}

export const broadcastIdleLogout = () => broadcast({ type: 'idle-logout' })

/*
** 서버 호출 (Apollo 밖에서 쓰는 것: 앱 시작, 모달)
*/
export class GqlError extends Error {
    code: string
    extensions: any

    constructor(error: any) {
        super(error && error.message)
        this.extensions = (error && error.extensions) || {}
        this.code = this.extensions.code || ''
    }
}

export const authHeaders = () => ({
    'x-lsv-csrf': '1'
})

export async function gqlFetch(query: string, variables: any = {}) {
    const res = await fetch(`${process.env.REACT_APP_API}/graphql`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'content-type': 'application/json',
            lang: cookies.get('NEXT_LOCALE') || '',
            ...authHeaders()
        },
        body: JSON.stringify({ query, variables })
    })

    const json = await res.json()

    if (json.errors && json.errors.length) throw new GqlError(json.errors[0])

    return json.data
}

export const isUnauthenticated = (e: any) => Boolean(e && e.code === 'UNAUTHENTICATED')

const ME = `query Me { me }`
const MIGRATE_SESSION = `mutation MigrateSession($token: String!) { migrateSession(token: $token) { status user_no } }`

export async function fetchMe() {
    const data = await gqlFetch(ME)
    return data.me as Me
}

/*
** 앱 시작
** 1. 업데이트 전 localStorage 토큰 → 쿠키 세션으로 한번 바꾼다 (유예기간)
** 2. me 로 로그인 확인
**    - 실패 + 이 브라우저에서 로그인 중이었음(Logined 쿠키) → 재로그인 모달
**    - 실패 + 표시 없음(이 브라우저에서 로그인한 적 없음, 로그아웃함) → 로그인 페이지 (Logined 쿠키도 지운다)
**    - 성공했어도 마지막 활동이 미사용 시간(IDLE_LOGOUT_MS) 넘었으면 (브라우저 껐다가 그 뒤 5분 안에 다시 엶. 그보다 늦으면 서버가 먼저 끊는다)
**      → 서버 세션 폐기 + 재로그인 모달 (다른 탭에서 쓰고 있었다면 활동 시각이 최근이라 해당 없음)
*/
let booted = false

export async function bootstrapSession() {
    if (booted || isRespondHost()) return
    booted = true

    let expectUserNo: number | null = null

    let legacyToken: string | null = null
    try {
        legacyToken = localStorage.getItem('authorization')
    } catch (e) {}

    if (legacyToken) {
        const legacy = decodeJwt(legacyToken)
        expectUserNo = legacy && legacy.user_no ? Number(legacy.user_no) : null

        try {
            localStorage.removeItem('authorization')
        } catch (e) {}

        try {
            const data = await gqlFetch(MIGRATE_SESSION, { token: legacyToken })
            if (data.migrateSession && data.migrateSession.user_no) setLoginedCookie(data.migrateSession.user_no)
        } catch (e) {
            // 바꿔줄수 없는 토큰 → 아래 me 실패 → 모달
            if (expectUserNo) setLoginedCookie(expectUserNo)
        }
    }

    try {
        const value = await fetchMe()

        if (value && value.user_no) {
            const stored = getStoredActivity()

            if (stored && Date.now() - stored >= IDLE_LOGOUT_MS) {
                await gqlFetch(`mutation Logout { logout }`).catch(() => {})
                markLoginedOut()
                relogin = { open: false, reason: 'IDLE', expectUserNo: Number(value.user_no) }
                return
            }

            setLoginedCookie(value.user_no)
            setMe(value, { broadcast: false })
        }
    } catch (e: any) {
        if (!isUnauthenticated(e)) return

        const loginedUserNo = expectUserNo || getLoginedUserNo()

        if (loginedUserNo) {
            markLoginedOut()

            const reason = (e.extensions && e.extensions.reason) || 'NONE'
            // 로그인 페이지 등에서는 띄우지 않는다. AuthRoute 화면에서 SessionGuard 가 연다
            relogin = { open: false, reason, expectUserNo: loginedUserNo }
        } else {
            // 재로그인 대상이 아닌데 Logined=true 만 남은 경우 (업데이트 전 로그인 등)
            // 남겨두면 www 로그인 → 작업함 → /logining 실패 → www 를 계속 반복한다
            removeLoginedCookie()
        }
    }
}

// 로그인이 필요한 화면에서 쓴다: 로그인 정보도 없고 재로그인 대상도 아니면 로그인 페이지로
export const hasLoginContext = () => Boolean(me) || Boolean(relogin.expectUserNo)

export const isAuthenticated = () => Boolean(me)

// 로그아웃 (서버 세션 폐기 + 표시 쿠키 제거)
export async function serverLogout() {
    try {
        await gqlFetch(`mutation Logout { logout }`)
    } catch (e) {}

    removeLoginedCookie()
    setMe(null, { broadcast: false })
}

// 사용자가 움직였을때 서버 미사용 시각 갱신 (탭, iframe 합쳐서 5분에 한번)
const TOUCH_KEY = 'lsv_last_touch'
const TOUCH_INTERVAL_MS = 5 * 60 * 1000

export const touchSessionIfNeeded = (force = false) => {
    if (!me) return

    let lastTouch = 0
    try {
        lastTouch = Number(localStorage.getItem(TOUCH_KEY)) || 0
    } catch (e) {}

    if (!force && Date.now() - lastTouch < TOUCH_INTERVAL_MS) return

    try {
        localStorage.setItem(TOUCH_KEY, String(Date.now()))
    } catch (e) {}

    gqlFetch(`mutation TouchSession { touchSession }`).catch(e => {
        if (isUnauthenticated(e)) openRelogin((e.extensions && e.extensions.reason) || 'NONE')
    })
}

// REST(axios) 요청: 쿠키 세션 + CSRF 헤더. 401 이면 재로그인 모달
export async function sessionAxios(config: AxiosRequestConfig) {
    try {
        return await axios({
            ...config,
            withCredentials: true,
            headers: { ...authHeaders(), ...(config.headers || {}) }
        })
    } catch (e: any) {
        if (e && e.response && e.response.status === 401) openRelogin('NONE')
        throw e
    }
}
